import { MEDIA_TARIFF_V1018 } from '../../components/mediaTariffV1018';

export const isMediaPriceV111 = (price) =>
  price?.meter === MEDIA_TARIFF_V1018;

const quantityOrder = [
  'output_seconds', 'output_count', 'output_tokens', 'output_pixels', 'calls',
  'input_seconds', 'input_images', 'input_tokens', 'input_pixels', 'audio_seconds',
];

export function selectMediaCardPricesV111(prices) {
  const groups = new Map();
  // Select actual rules, not a minimum across incompatible units/specifications.
  // Separate additive groups and steps; all alternatives remain in the details.
  prices.filter(isMediaPriceV111).forEach((price) => {
    const a = price.attributes || {};
    const key = JSON.stringify([
      a.media_kind, a.group, a.quantity, a.step || '1',
      a.rounding || 'exact', a.service_tier || 'standard', !!a.optional,
    ]);
    if (!groups.has(key)) groups.set(key, { ...price, summaryKeyV111: key });
  });
  return [...groups.values()].sort((left, right) => {
    const rank = (price) => {
      const a = price.attributes || {};
      const quantity = quantityOrder.indexOf(a.quantity);
      return (a.optional ? 100 : 0) +
        (a.service_tier && a.service_tier !== 'standard' ? 50 : 0) +
        (quantity < 0 ? quantityOrder.length : quantity);
    };
    return rank(left) - rank(right);
  }).slice(0, 4);
}

const text = (t, key, options = {}) =>
  t(`models.catalog_v10.media_price.${key}`, options);

export const mediaPriceLabelV111 = (price, t) =>
  text(t, `quantities.${price?.attributes?.quantity}`, {
    defaultValue: price?.attributes?.quantity || t('models.commercial.meters.media_tariff'),
  });

export function mediaPriceUnitV111(price, t) {
  const a = price?.attributes || {};
  const quantityUnits = {
    calls: 'call', output_count: a.media_kind === 'video' ? 'video' :
      a.media_kind === 'image' ? 'image' : 'item',
    output_seconds: 'second', input_seconds: 'second', audio_seconds: 'second',
    input_images: 'image', input_tokens: 'token', output_tokens: 'token',
    input_pixels: 'pixel', output_pixels: 'pixel',
  };
  const unit = quantityUnits[a.quantity];
  if (!unit) return t(`models.catalog_v10.units.${price.unit}`, {
    defaultValue: `/${price.unit || 'unit'}`,
  });
  const step = String(a.step ?? '1');
  return text(t, 'per', {
    step: /^1(?:\.0+)?$/.test(step) ? '' : `${step} `,
    unit: text(t, `units.${unit}`),
  });
}

export function mediaPriceScopeV111(attributes, t, detailed = false) {
  const a = attributes || {};
  const parts = [];
  for (const [field, operator] of [['when', '='], ['when_min', '>='], ['when_max', '<=']]) {
    for (const [key, value] of Object.entries(a[field] || {})) {
      const label = text(t, `dimensions.${key}`, { defaultValue: key });
      const display = ['true', 'false'].includes(String(value)) ?
        text(t, String(value) === 'true' ? 'yes' : 'no') : String(value);
      parts.push(`${label} ${operator} ${display}`);
    }
  }
  if (detailed || a.to !== undefined || !/^0(?:\.0+)?$/.test(String(a.from ?? '0'))) {
    parts.push(text(t, 'interval', {
      from: a.from ?? '0', to: a.to ?? text(t, 'unbounded'),
    }));
  }
  if (a.rounding === 'ceil') parts.push(text(t, 'ceil'));
  if (a.service_tier && a.service_tier !== 'standard')
    parts.push(text(t, 'tier', { tier: a.service_tier }));
  if (a.optional) parts.push(text(t, 'optional'));
  if (detailed && a.group) parts.push(text(t, 'group', { group: a.group }));
  return parts.join(' / ') || text(t, 'standard');
}
